import { defineStore } from "pinia";
import { ref } from "vue";
import { ApiClientFactory } from "@/stores/apiClientFactory";
import { UploadManyImagesRequest, ImageUploadItem, DeleteManyImagesRequest, ImageHousekeepingCleanUpRequest, RestoreTrashedImagesRequest, RestoreTrashedImagesResponse, JobResultOfImageHousekeepingScanStatusResponse, JobResultOfImageHousekeepingCleanUpStatusResponse } from "@/api-client/models";
import { handleGlobalError, isBenignKiotaResponseParseError } from "@/composables/globalErrorHandler";
import { useAuthStore } from '@/stores/authStore';
import { MultipartBody } from '@microsoft/kiota-abstractions';

/**
 * What the API assigned an uploaded image to: the product whose SKU or image name matched, else the
 * product family whose key matched, else nothing (the image is stored but shows nowhere).
 * `unknown` is an API that predates the report and answered 204 with no body.
 */
export type ImageUploadResult = {
    imageName: string;
    assignedTo: 'product' | 'productFamily' | 'none' | 'unknown';
    assignedToKey?: string | null;
};

export const useImageStore = defineStore('imageStore', () => {
    
    const authStore = useAuthStore();
    const clientFactory = ApiClientFactory.getInstance();
    const client = clientFactory.getClient();
    const baseApiUrl = process.env.VITE_MOMENTUM_API_BASE_URL || '';

    //State
    const orgImageLoading = ref(false);
    const loading = ref(false);
    const imagesResponse = ref<any>(null);

    //Actions
    const createOrganizationImage = async (logo: File): Promise<string | undefined> => {
        orgImageLoading.value = true;
        try {
            const formData = new FormData();
            formData.append("logo", logo);

            const authToken = await authStore.getAuthorizationToken();
            const response = await fetch(`${baseApiUrl}/api/images/organization-logo`, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + authToken,
                },
                body: formData,
            });

            const jsonResponse = await response.json();
            return jsonResponse.logoUrl;

        } catch (error) {
            handleGlobalError(error);
        } finally {
            orgImageLoading.value = false;
        }
    }

    // Action to upload an image under a key: a product's SKU or a family's key. Throws when the
    // upload fails, so callers can tell a failed file from a stored one.
    const uploadProductImage = async (sku: string, image: File): Promise<ImageUploadResult> => {
        const formData = new FormData();
        formData.append("image", image);
        formData.append("sku", sku);

        const authToken = await authStore.getAuthorizationToken();
        const response = await fetch(`${baseApiUrl}/api/images/from-local`, {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + authToken,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Uploading ${image.name} failed (${response.status})`);
        }

        if (response.status === 204) {
            return { imageName: image.name, assignedTo: 'unknown' };
        }

        const result = await response.json();
        return {
            imageName: result.imageName ?? image.name,
            assignedTo: result.assignedTo ?? 'unknown',
            assignedToKey: result.assignedToKey ?? null
        };
    }

    // Action to upload a category image
    const uploadCategoryImage = async (categoryName: string, image: File) => {
        try {
        
            const formData = new FormData();
            formData.append("image", image);
            formData.append("categoryName", categoryName);

            const authToken = await authStore.getAuthorizationToken();
            const response = await fetch(`${baseApiUrl}/api/images/category?categoryName=${categoryName}`, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + authToken,
                },
                body: formData, 
            });
            
            const jsonResponse = await response.json();
            return jsonResponse.imageUrl;

        } catch (error) {
            handleGlobalError(error);
        }
    };

    // Action to upload multiple images
    const importImagesFromRemote = async (images: ImageUploadItem[]) => {
        const uploadRequest: UploadManyImagesRequest = {
            images: images
        };
        try {
            const response = await client.api.images.fromRemote.post(uploadRequest);
            return response;
        } catch (error) {
            if (isBenignKiotaResponseParseError(error)) return undefined;
            handleGlobalError(error);
            throw error;
        }
    };

    // Action to list images
    const listImages = async (pagingParameters: any) => {
        loading.value = true;
        imagesResponse.value = null;
        try {
            const params = {
                queryParameters: {
                    pageNumber: pagingParameters.PageNumber,
                    pageSize: pagingParameters.rows,
                    sortOn: pagingParameters.SortFieldProperCase,
                    sortDirection: pagingParameters.SortOrderAsString,
                    filters: pagingParameters.CleanedFilters ? [JSON.stringify(pagingParameters.CleanedFilters)] : []
                }
            };
            console.log('params', params);
            const response = await client.api.images.get(params);
            imagesResponse.value = response;
        } catch (error) {
            handleGlobalError(error);
        } finally {
            loading.value = false;
        }
    };

    // Action to delete many images
    const deleteImages = async (deleteImages: DeleteManyImagesRequest) => {
        try {
            const response = await client.api.images.delete(deleteImages);
            return response;
        } catch (error) {
            handleGlobalError(error);
        }
    };

    // Action to delete an image
    const deleteImage = async (imageId: string) => {
        try {
            const deleteRequest: DeleteManyImagesRequest = {
                ids: imageId
            };
            const response = await client.api.images.delete(deleteRequest);
            return response;
        } catch (error) {
            handleGlobalError(error);
        }
    };

    // Org-scoped housekeeping (CU-86bbvgvue): scan and clean-up are queued jobs the UI polls by
    // tracking id; the server re-validates every clean-up id, so the selection is advisory only.
    const startHousekeepingScan = async (): Promise<string | undefined> => {
        try {
            const response = await client.api.images.housekeeping.scan.post();
            return response?.trackingId ?? undefined;
        } catch (error) {
            handleGlobalError(error);
        }
    };

    const getHousekeepingScanProgress = async (trackingId: string): Promise<JobResultOfImageHousekeepingScanStatusResponse | undefined> => {
        return await client.api.images.housekeeping.scan.progress.byTrackingId(trackingId).post();
    };

    const startHousekeepingCleanUp = async (imageIds: string[]): Promise<string | undefined> => {
        try {
            const request: ImageHousekeepingCleanUpRequest = { imageIds };
            const response = await client.api.images.housekeeping.cleanUp.post(request);
            return response?.trackingId ?? undefined;
        } catch (error) {
            handleGlobalError(error);
        }
    };

    const getHousekeepingCleanUpProgress = async (trackingId: string): Promise<JobResultOfImageHousekeepingCleanUpStatusResponse | undefined> => {
        return await client.api.images.housekeeping.cleanUp.progress.byTrackingId(trackingId).post();
    };

    const restoreTrashedImages = async (imageIds: string[]): Promise<RestoreTrashedImagesResponse | undefined> => {
        try {
            const request: RestoreTrashedImagesRequest = { imageIds };
            return await client.api.images.trash.restore.post(request);
        } catch (error) {
            if (isBenignKiotaResponseParseError(error)) return undefined;
            handleGlobalError(error);
            throw error;
        }
    };

    return {
        orgImageLoading,
        loading,
        imagesResponse,
        startHousekeepingScan,
        getHousekeepingScanProgress,
        startHousekeepingCleanUp,
        getHousekeepingCleanUpProgress,
        restoreTrashedImages,
        createOrganizationImage,
        importImagesFromRemote,
        listImages,
        deleteImage,
        deleteImages,
        uploadCategoryImage,
        uploadProductImage
    };
});
